<script setup lang="ts">
import { computed } from 'vue'
import { Input } from '@/components/shadcn'

type StyleType =
  | 'default'   // 기본 스타일
  | 'error'     // 에러 상태 (빨강 테두리)
  | 'success'   // 성공 상태 (초록 테두리)
  | 'warning'   // 경고 상태 (주황 테두리)
  | 'sm'        // 작은 크기
  | 'lg'        // 큰 크기

const props = withDefaults(
  defineProps<{
    modelValue?: string | number
    type?: string
    placeholder?: string
    disabled?: boolean
    readonly?: boolean
    required?: boolean
    name?: string
    id?: string
    class?: string
    /** 스타일 프리셋 */
    styletype?: StyleType
  }>(),
  {
    type: 'text',
    placeholder: '',
    disabled: false,
    readonly: false,
    required: false,
    styletype: 'default',
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: string | number]
  'change': [value: string | number]
  'focus': [event: FocusEvent]
  'blur': [event: FocusEvent]
}>()

/**
 * styletype -> class 매핑
 */
const STYLE_PRESETS: Record<StyleType, { class: string }> = {
  default: { class: '' },
  error: { 
    class: 'border-destructive focus-visible:ring-destructive',
  },
  success: { 
    class: 'border-green-500 focus-visible:ring-green-500',
  },
  warning: { 
    class: 'border-amber-500 focus-visible:ring-amber-500',
  },
  sm: { 
    class: 'h-7 text-xs px-2',
  },
  lg: { 
    class: 'h-12 text-base px-4',
  },
}

/** 최종 바인딩: 직접 넘긴 class가 있으면 styletype 기본값과 병합 */
const mapped = computed(() => {
  const styleKey = props.styletype || 'default'
  const preset = STYLE_PRESETS[styleKey] ?? STYLE_PRESETS.default
  const finalClass = [preset?.class, props.class].filter(Boolean).join(' ')
  
  return {
    type: props.type,
    placeholder: props.placeholder,
    disabled: props.disabled,
    readonly: props.readonly,
    required: props.required,
    name: props.name,
    id: props.id,
    class: finalClass,
  }
})

const handleInput = (value: string | number) => {
  emit('update:modelValue', value)
  emit('change', value)
}
</script>

<template>
  <Input 
    v-bind="mapped" 
    :model-value="modelValue" 
    @update:model-value="handleInput"
    @focus="emit('focus', $event)"
    @blur="emit('blur', $event)"
  >
    <slot />
  </Input>
</template>
